const DAY_MS = 24 * 60 * 60 * 1000;
const VALID_PRORATION_RATIOS = new Set(['day', 'week', 'month']);
const VALID_FULL_PRICE_WINDOW_UNITS = new Set(['day', 'month']);

function toInt(value, fallback) {
  const n = Number(value);
  return Number.isInteger(n) ? n : fallback;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function utcDate(year, month, day) {
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const safeDay = clamp(day, 1, daysInMonth);
  return new Date(Date.UTC(year, month - 1, safeDay, 0, 0, 0, 0));
}

function startOfUtcDay(date) {
  return utcDate(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate()
  );
}

function diffUtcDays(startDate, endDate) {
  const start = startOfUtcDay(startDate);
  const end = startOfUtcDay(endDate);
  return Math.max(0, Math.round((end - start) / DAY_MS));
}

function buildMonthlyBucketStarts(cycleStart, cycleEnd, anchorDay) {
  const starts = [startOfUtcDay(cycleStart)];
  let monthOffset = 1;
  while (monthOffset < 24) {
    const next = utcDate(
      cycleStart.getUTCFullYear(),
      cycleStart.getUTCMonth() + 1 + monthOffset,
      anchorDay
    );
    if (next >= cycleEnd) break;
    starts.push(next);
    monthOffset += 1;
  }
  return starts;
}

function countRemainingMonthlyBuckets(asOfDate, cycleStart, cycleEnd, anchorDay) {
  const today = startOfUtcDay(asOfDate);
  const starts = buildMonthlyBucketStarts(cycleStart, cycleEnd, anchorDay);
  const totalMonths = starts.length;
  if (today <= cycleStart) {
    return { totalMonths, remainingMonths: totalMonths };
  }
  if (today >= cycleEnd) {
    return { totalMonths, remainingMonths: 0 };
  }

  let currentIndex = 0;
  for (let index = 0; index < starts.length; index += 1) {
    if (today >= starts[index]) currentIndex = index;
    else break;
  }

  return {
    totalMonths,
    remainingMonths: Math.max(0, totalMonths - currentIndex),
  };
}

export function resolveMembershipYearProrationRatio(setup = {}) {
  const rawValue = typeof setup?.membershipYearProrationRatio === 'string'
    ? setup.membershipYearProrationRatio.trim().toLowerCase()
    : '';
  return VALID_PRORATION_RATIOS.has(rawValue) ? rawValue : 'day';
}

export function resolveMembershipYearFullPriceWindow(setup = {}) {
  const rawWindow = setup?.membershipYearFullPriceWindow;
  const unit = VALID_FULL_PRICE_WINDOW_UNITS.has(rawWindow?.unit)
    ? rawWindow.unit
    : 'month';
  const maximum = unit === 'day' ? 366 : 12;
  const parsedValue = Number(rawWindow?.value);
  const value = Number.isInteger(parsedValue)
    ? clamp(parsedValue, 0, maximum)
    : 0;
  return { value, unit };
}

function addUtcDays(date, days) {
  return new Date(startOfUtcDay(date).getTime() + (days * DAY_MS));
}

function addUtcMonths(date, months) {
  const start = startOfUtcDay(date);
  return utcDate(
    start.getUTCFullYear(),
    start.getUTCMonth() + 1 + months,
    start.getUTCDate()
  );
}

export function calculateMembershipYearProration({
  asOfDate,
  cycleStart,
  cycleEnd,
  ratio = 'day',
  monthAnchorDay = 1,
} = {}) {
  const effectiveRatio = VALID_PRORATION_RATIOS.has(ratio) ? ratio : 'day';
  const rangeStart = startOfUtcDay(asOfDate ? new Date(asOfDate) : new Date());
  const normalizedCycleStart = startOfUtcDay(cycleStart ? new Date(cycleStart) : rangeStart);
  const normalizedCycleEnd = startOfUtcDay(cycleEnd ? new Date(cycleEnd) : normalizedCycleStart);
  const cycleDays = Math.max(1, diffUtcDays(normalizedCycleStart, normalizedCycleEnd));
  const remainingDays = diffUtcDays(rangeStart, normalizedCycleEnd);

  if (effectiveRatio === 'week') {
    const cycleWeeks = Math.max(1, Math.ceil(cycleDays / 7));
    const remainingWeeks = remainingDays > 0 ? Math.ceil(remainingDays / 7) : 0;
    return {
      ratio: effectiveRatio,
      cycleDays,
      remainingDays,
      cycleWeeks,
      remainingWeeks,
      cycleMonths: 12,
      remainingMonths: null,
      factor: clamp(remainingWeeks / cycleWeeks, 0, 1),
    };
  }

  if (effectiveRatio === 'month') {
    const { totalMonths, remainingMonths } = countRemainingMonthlyBuckets(
      rangeStart,
      normalizedCycleStart,
      normalizedCycleEnd,
      monthAnchorDay
    );
    return {
      ratio: effectiveRatio,
      cycleDays,
      remainingDays,
      cycleWeeks: Math.max(1, Math.ceil(cycleDays / 7)),
      remainingWeeks: remainingDays > 0 ? Math.ceil(remainingDays / 7) : 0,
      cycleMonths: totalMonths,
      remainingMonths,
      factor: clamp(remainingMonths / Math.max(1, totalMonths), 0, 1),
    };
  }

  return {
    ratio: 'day',
    cycleDays,
    remainingDays,
    cycleWeeks: Math.max(1, Math.ceil(cycleDays / 7)),
    remainingWeeks: remainingDays > 0 ? Math.ceil(remainingDays / 7) : 0,
    cycleMonths: 12,
    remainingMonths: null,
    factor: clamp(remainingDays / cycleDays, 0, 1),
  };
}

export function getMembershipYearConfig(setup = {}) {
  const enabled = Boolean(setup.membershipYearEnabled);
  const startMonth = clamp(toInt(setup.membershipYearStartMonth, 1), 1, 12);
  const startDay = clamp(toInt(setup.membershipYearStartDay, 1), 1, 31);
  const prorationRatio = resolveMembershipYearProrationRatio(setup);
  const fullPriceWindow = resolveMembershipYearFullPriceWindow(setup);
  return { enabled, startMonth, startDay, prorationRatio, fullPriceWindow };
}

export function getMembershipYearWindow(asOfDate, setup = {}) {
  const now = asOfDate ? new Date(asOfDate) : new Date();
  const today = startOfUtcDay(now);
  const { enabled, startMonth, startDay, prorationRatio, fullPriceWindow } = getMembershipYearConfig(setup);
  const anchorThisYear = utcDate(today.getUTCFullYear(), startMonth, startDay);
  const currentYearStart = today >= anchorThisYear
    ? anchorThisYear
    : utcDate(today.getUTCFullYear() - 1, startMonth, startDay);
  const nextYearStart = utcDate(
    currentYearStart.getUTCFullYear() + 1,
    startMonth,
    startDay
  );
  const proration = calculateMembershipYearProration({
    asOfDate: today,
    cycleStart: currentYearStart,
    cycleEnd: nextYearStart,
    ratio: prorationRatio,
    monthAnchorDay: startDay,
  });
  const requestedFullPriceWindowEnd = fullPriceWindow.unit === 'day'
    ? addUtcDays(currentYearStart, fullPriceWindow.value)
    : addUtcMonths(currentYearStart, fullPriceWindow.value);
  const fullPriceWindowEnd = requestedFullPriceWindowEnd < nextYearStart
    ? requestedFullPriceWindowEnd
    : nextYearStart;
  const fullPriceWindowActive = enabled
    && fullPriceWindow.value > 0
    && today < fullPriceWindowEnd;
  const billingCycleKey = `${currentYearStart.getUTCFullYear()}-${String(startMonth).padStart(2, '0')}`;

  return {
    enabled,
    startMonth,
    startDay,
    prorationRatio,
    fullPriceWindow,
    fullPriceWindowEnd,
    fullPriceWindowActive,
    currentYearStart,
    nextYearStart,
    cycleDays: proration.cycleDays,
    remainingDays: proration.remainingDays,
    cycleWeeks: proration.cycleWeeks,
    remainingWeeks: proration.remainingWeeks,
    cycleMonths: proration.cycleMonths,
    remainingMonths: proration.remainingMonths,
    prorationFactor: fullPriceWindowActive ? 1 : proration.factor,
    billingCycleKey,
  };
}

export function isAnnualBoltOn(boltOn = {}) {
  return boltOn?.hiddenFromBoltOns !== true
    && String(boltOn?.source || '') !== 'moorings'
    && String(boltOn?.renewalUnit || 'year') === 'year'
    && Number(boltOn?.renewalEvery || 1) === 1;
}

export function getBoltOnRenewalSettings(setup = {}) {
  const custom = setup.boltOnRenewalDateSource === 'custom';
  return {
    enabled: setup.boltOnRenewalDateSource !== 'purchase' && (custom || setup.membershipYearEnabled === true),
    month: custom ? setup.boltOnRenewalMonth : setup.membershipYearStartMonth,
    day: custom ? setup.boltOnRenewalDay : setup.membershipYearStartDay,
  };
}

export function isBoltOnMembershipYearAligned(setup = {}, boltOn = {}) {
  if (!getBoltOnRenewalSettings(setup).enabled || !isAnnualBoltOn(boltOn)) return false;
  const mode = ['off', 'all', 'selected'].includes(setup?.boltOnMembershipYearMode)
    ? setup.boltOnMembershipYearMode
    : 'off';
  return mode === 'all' || (mode === 'selected' && boltOn?.membershipYearAligned === true);
}

export function getBoltOnMembershipYearWindow(asOfDate, setup = {}, boltOn = {}) {
  const renewal = getBoltOnRenewalSettings(setup);
  return getMembershipYearWindow(asOfDate, {
    membershipYearEnabled: isBoltOnMembershipYearAligned(setup, boltOn),
    membershipYearStartMonth: renewal.month,
    membershipYearStartDay: renewal.day,
    membershipYearProrationRatio: setup?.boltOnMembershipYearProrationRatio,
    membershipYearFullPriceWindow: setup?.boltOnMembershipYearFullPriceWindow,
  });
}
